<template>
  <div class="favourites bg-white">
    <div class="container mx-auto px-3 bg-white">
      <div v-if="message" class="message">{{ message }}</div>
      <div
        v-for="livingOption in structuredPropertyData"
        class="living-option"
        :key="livingOption._id"
      >
        <h2
          class="living-option__heading"
          :dir="lang.dir"
          v-html="
            livingOption.displayName[lang.code] ||
              livingOption.displayName['en']
          "
        ></h2>
        <div
          class="living-option__description"
          :dir="lang.dir"
          v-if="
            livingOption.description[lang.code] ||
              livingOption.description['en']
          "
          v-html="
            livingOption.description[lang.code] ||
              livingOption.description['en']
          "
        ></div>
        <div class="living-option__buttons">
          <a
            v-if="livingOption.brochure"
            :href="$config.assetsHost + livingOption.brochure"
            target="_blank"
            class="button mr-3 mb-3"
            >View Brochure</a
          >
          <button
            v-if="livingOption.images.length"
            class="button mr-3 mb-3"
            @click="
              openLightboxSlider(
                livingOption.images.map(
                  (image) => `${$store.state.ASSETS_HOST}/${image}`
                )
              )
            "
          >
            {{
              $store.state.labels.viewImages[lang.code] ||
                $store.state.labels.viewImages["en"]
            }}
          </button>
          <button
            v-if="livingOption.videos.length"
            class="button mr-3 mb-3"
            @click="
              openLightboxSlider(
                livingOption.videos.map(
                  (video) => `${$store.state.ASSETS_HOST}/${video}`
                )
              )
            "
          >
            {{
              $store.state.labels.viewVideos[lang.code] ||
                $store.state.labels.viewVideos["en"]
            }}
          </button>
        </div>
        <div class="properties">
          <div
            v-for="property in livingOption.properties"
            class="property"
            :key="property._id"
          >
            <div class="property__body" :dir="lang.dir">
              <h3
                v-if="
                  property.label && property.dwelling && property.dwelling.label
                "
                class="property__heading"
                v-html="
                  `${property.displayLabel[lang.code] ||
                    property.displayLabel['en'] ||
                    property.label} - ${property.dwelling.displayName[
                    lang.code
                  ] ||
                    property.dwelling.displayName['en'] ||
                    property.dwelling.label}`
                "
              ></h3>
              <h3
                v-if="property.label && !property.dwelling"
                class="property__heading"
                v-html="
                  `${property.displayLabel[lang.code] ||
                    property.displayLabel['en'] ||
                    property.label}`
                "
              ></h3>

              <div
                class="property__subsection-label"
                v-html="
                  property.subsection.displayName[lang.code] ||
                    property.subsection.displayName['en']
                "
              ></div>
              <table class="property__details">
                <tr v-if="property.price" class="property__detail">
                  <td>Price</td>
                  <td>{{ priceToCurrency(property.price) }}</td>
                </tr>
                <tr
                  v-else-if="property.dwelling && property.dwelling.price"
                  class="property__detail"
                >
                  <td>Price</td>
                  <td>
                    {{ priceToCurrency(property.dwelling.price) }}
                  </td>
                </tr>
                <tr
                  v-if="
                    property.dwelling &&
                      property.dwelling.houseDesign &&
                      (property.dwelling.houseDesign[lang.code] ||
                        property.dwelling.houseDesign['en'])
                  "
                  class="property__detail"
                >
                  <td>House Design</td>
                  <td>
                    {{
                      property.dwelling.houseDesign[lang.code] ||
                        property.dwelling.houseDesign["en"]
                    }}
                  </td>
                </tr>
                <tr v-if="property.size" class="property__detail">
                  <td>Lot Size</td>
                  <td>
                    {{ property.size }} <span>m<sup>2</sup></span>
                  </td>
                </tr>
                <tr
                  v-if="property.dwelling && property.dwelling.size"
                  class="property__detail"
                >
                  <td>House Size</td>
                  <td>
                    <span dir="ltr">
                      {{ property.dwelling.size }} <span>m<sup>2</sup></span>
                    </span>
                  </td>
                </tr>
                <tr
                  v-if="property.dwelling && property.dwelling.beds"
                  class="property__detail"
                >
                  <td>Bedrooms</td>
                  <td>{{ property.dwelling.beds }}</td>
                </tr>
                <tr
                  v-if="property.dwelling && property.dwelling.baths"
                  class="property__detail"
                >
                  <td>Bathrooms</td>
                  <td>{{ property.dwelling.baths }}</td>
                </tr>
                <tr
                  v-if="property.dwelling && property.dwelling.cars"
                  class="property__detail"
                >
                  <td>Parking</td>
                  <td>{{ property.dwelling.cars }}</td>
                </tr>
              </table>

              <div class="spacer"></div>
              <div>
                <button
                  v-if="parsePropertyImages(property).length"
                  class="button button--dark"
                  @click="openLightboxSlider(parsePropertyImages(property))"
                >
                  {{
                    $store.state.labels.viewImages[lang.code] ||
                      $store.state.labels.viewImages["en"]
                  }}
                </button>
              </div>
            </div>
            <div class="property__image">
              <v-img
                :src="getPropertyThumbnail(property)"
                fit="cover"
                @click.native="
                  openLightboxSlider(parsePropertyImages(property))
                "
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <lightbox-slider
      :active="isLightboxActive"
      :images="lightboxImages"
      @close="closeLightboxSlider"
    />
  </div>
</template>

<script>
import LightboxSlider from "./LightboxSlider";
import VImg from "./VImage";
import { mapGetters, mapState } from "vuex";

export default {
  components: { LightboxSlider, VImg },

  data() {
    return {
      isLightboxActive: false,
      lightboxImages: [],
    };
  },

  computed: {
    ...mapState(["lang"]),
    ...mapGetters(["message", "structuredPropertyData"]),
  },

  filters: {
    money(value) {
      if (!value) return "";
      let remaining = value.toString(); // initially, the entire value is "remaining"
      let formatted = ""; // this is the return value string that we add the formatted '000s to
      while (remaining.length > 3) {
        // 3 because ,000 (thousand)
        formatted =
          "," +
          remaining.substring(remaining.length - 3, remaining.length) +
          formatted; // , + <next 3 digits> + <any already formatted digits>
        remaining = remaining.substring(0, remaining.length - 3); // remove the last 3 digits from remaining
      }
      formatted = remaining + formatted; // < 3 digits left so no more ',' to add
      return formatted;
    },
  },

  mounted() {
    setTimeout(() => {
      this.$store.commit("LEADFORM_TIMEOUT_FINISHED");
    }, 3000);
  },

  methods: {
    closeLightboxSlider() {
      this.isLightboxActive = false;
      this.lightboxImages = [];
    },

    getPropertyThumbnail(property) {
      return this.parsePropertyImages(property)[0];
    },

    openLightboxSlider(images) {
      this.isLightboxActive = true;
      this.lightboxImages = images;
    },

    parsePropertyImages(property) {
      const subsectionFloorplan =
        property.subsection.backgroundImage &&
        property.subsection.backgroundImage[this.lang.code]
          ? [
              `${this.$store.state.ASSETS_HOST}/${
                property.subsection.backgroundImage[this.lang.code]
              }`,
            ]
          : [];
      const propertyViewpointImages = property.viewImages.map(
        (image) => `${this.$store.state.ASSETS_HOST}/${image}`
      );
      const propertyGalleryImages = property.galleryImages.map(
        (image) => `${this.$store.state.ASSETS_HOST}/${image}`
      );
      const propertyFacadeImages = property.dwelling
        ? property.dwelling.facadeImages.map(
            (image) => `${this.$store.state.ASSETS_HOST}/${image}`
          )
        : [];
      const propertyModelGalleryImages = property.dwelling
        ? property.dwelling.galleryImages.map(
            (image) => `${this.$store.state.ASSETS_HOST}/${image}`
          )
        : [];
      // const propertyModelFloorplanImages = property.dwelling
      //   ? property.dwelling.floorplanImages[this.lang.code].map(
      //       (image) => `${this.$store.state.ASSETS_HOST}/${image}`
      //     )
      //   : [];

      // const images = property.dwelling
      //   ? [
      //       ...propertyFacadeImages,

      const fpImages = property.dwelling
        ? property.dwelling.floorplanImages[this.lang.code] ||
          property.dwelling.floorplanImages.en
        : [];

      const propertyModelFloorplanImages =
        property.dwelling && fpImages
          ? fpImages.map((image) => `${this.$store.state.ASSETS_HOST}/${image}`)
          : [];

      return property.dwelling
        ? [
            ...propertyFacadeImages,
            ...propertyModelGalleryImages,
            ...propertyGalleryImages,
            ...propertyModelFloorplanImages,
            ...propertyViewpointImages,
            ...subsectionFloorplan,
          ]
        : [
            ...propertyFacadeImages,
            ...propertyViewpointImages,
            ...propertyGalleryImages,
            ...propertyModelFloorplanImages,
            ...propertyModelGalleryImages,
            ...subsectionFloorplan,
          ];

      // return unique array
      // return images.filter((image, i, self) => i === self.indexOf(image));
    },

    parsePropertyNumber(value) {
      if (!value) return "";
      let explodedString = value.split("-");
      return explodedString[explodedString.length - 1];
    },

    priceToCurrency(price) {
      return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: this.$config.currency.code,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(price);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/styles/_variables";

.message {
  margin-bottom: 20px;
}

.living-option {
  margin-bottom: 30px;

  &__heading {
    border-top: 1px solid #000;
    color: #000;
    font-size: 24px;
    font-weight: 600;
    line-height: 1;
    margin-bottom: 0.8em;
    padding-top: 0.5em;
  }

  &__description {
    p {
      color: #000;
      line-height: 1.4;
      margin-bottom: 30px;
      max-width: 600px;
    }
  }

  &__buttons {
    margin-bottom: 15px;
  }
}

.properties {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}

.property {
  background-color: $brand-color;
  color: var(--text-color);
  display: flex;
  margin-bottom: 15px;
  padding: 15px;
  width: calc(50% - 7.5px);

  @media (max-width: 1279px) {
    width: 100%;
  }

  @media (max-width: 600px) {
    display: block;
  }

  &__body {
    color: #fff;
    display: flex;
    flex-direction: column;
    padding: 15px;
    width: 40%;

    @media (max-width: 600px) {
      display: block;
      margin-bottom: 15px;
      padding: 15px 0;
      width: 100%;
    }
  }

  &__heading {
    color: var(--text-color);
    font-size: 18px;
    margin-bottom: 10px;
    text-transform: capitalize;
  }

  &__subsection-label {
    margin-bottom: 15px;
    opacity: 0.8;
  }

  &__details {
    margin-bottom: 15px;
  }

  &__detail {
    margin-bottom: 0.5em;

    td:first-child {
      font-size: 0.8em;
      opacity: 0.6;
      text-align: right;
      text-transform: uppercase;
      padding: 0 10px 10px 0;

      [dir="rtl"] & {
        padding: 0 0 10px 10px;
      }
    }

    td:last-child {
      opacity: 1;
      // width: 100%;

      span {
        font-size: 12px;
        opacity: 0.8;
      }
    }
  }

  &__image {
    cursor: pointer;
    // padding-left: 15px;
    // padding-bottom: 35%;
    position: relative;
    width: 60%;

    @media (max-width: 600px) {
      padding-bottom: 0;
      width: 100%;
    }

    // .v-img {
    //   position: absolute;
    //   top: 0;
    //   left: 0;
    //   height: 100%;
    //   width: 100%;

    //   @media (max-width: 600px) {
    //     position: relative;
    //   }
    // }
  }
}

.button {
  background-color: $brand-color;
  border: none;
  border-radius: 0;
  color: var(--text-color);
  display: inline-block;
  font-family: $font-sans;
  font-size: 12px;
  letter-spacing: 0.1em;
  padding: 0.8em 1.5em;
  text-transform: uppercase;

  &:hover,
  &:focus {
    // background-color: lighten($gold, 10%);
    opacity: 0.8;
    outline: none;
  }

  &--dark {
    background-color: $brand-color;
    border: 1px solid var(--text-color);
    color: var(--text-color);

    &:hover,
    &:focus {
      background-color: rgba(var(--text-color), 0.2);
      outline: none;
    }
  }
}

.spacer {
  flex-grow: 1;
}
</style>
